import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { finalizeLogin } from "../../app/store/auth";
import {
  useLazySendSecurityCodeToEmailQuery,
  useLazyVerifySecurityCodeQuery,
  useAddHostDetailsMutation,
} from "../../app/store/authApiSlice";
import { getAlert } from "../../Services/appAlerts";

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "";

  const [name, domain] = email.split("@");
  return `${name.slice(0, 2)}.....@${domain}`;
};

const ValidateUser = () => {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeToEmail, setCodeToEmail] = useState(false);
  const [codeToMobile, setCodeToMobile] = useState(false);
  const [verifyBlock, setVerifyBlock] = useState(false);
  const [reSendCodeMsg, setReSendCodeMsg] = useState(false);
  const [btnEmailLoading, setBtnEmailLoading] = useState(false);
  const [verifyBtnLoding, setVerifyBtnLoding] = useState(false);
  const [reSendCodeBtnLoding, setReSendCodeBtnLoding] = useState(false);

  const usrType = localStorage.getItem("userType");

  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sendSecurityCodeToEmail, {
    data: sendOtpData,
    isSuccess: isSendOtpSuccess,
    isError: isSendOtpError,
  }] = useLazySendSecurityCodeToEmailQuery();

  const [verifySecurityCode, {
    data: verifyOtpData,
    isSuccess: isVerifyOtpSuccess,
    isError: isVerifyOtpError,
  }] = useLazyVerifySecurityCodeQuery();

  const [addHostDetails] = useAddHostDetailsMutation();

  useEffect(() => {
    if (!currentUser && !token) {
      navigate("/");
    }
    if (currentUser && token) {
      navigate("/");
    }
  }, [currentUser, token, navigate]);

  useEffect(() => {
    if (isSendOtpSuccess) {
      if (sendOtpData === "success") {
        setCodeToEmail(true);
        setVerifyBlock(true);
        setCodeToMobile(false);
        setBtnEmailLoading(false);
        setReSendCodeMsg(reSendCodeBtnLoding);
        setReSendCodeBtnLoding(false);
      } else {
        getAlert("", "Authentication Failed", "error");
        setBtnEmailLoading(false);
        setReSendCodeBtnLoding(false);
      }
    }
    if (isSendOtpError) {
      getAlert("", "Authentication Failed", "error");
      setBtnEmailLoading(false);
      setReSendCodeBtnLoding(false);
    }
  }, [isSendOtpSuccess, sendOtpData, isSendOtpError, reSendCodeBtnLoding]);

  useEffect(() => {
    if (isVerifyOtpSuccess) {
      if (verifyOtpData === "success") {
        dispatch(finalizeLogin());
        if (currentUser?.id) {
          addHostDetails({ userId: currentUser.id, email: currentUser.email });
        }
        setVerifyBtnLoding(false);
        getAlert("", "Authenticated Successfully", "success");
        setCode("");
        setCodeError("");
        // Cleanup temporary storage items
        localStorage.removeItem("challengeId");
        localStorage.removeItem("tempPwd");
      } else {
        setCodeError("Invalid Code");
        setVerifyBtnLoding(false);
      }
    }

    if (isVerifyOtpError) {
      setCodeError("Invalid Code");
      setVerifyBtnLoding(false);
    }
  }, [isVerifyOtpSuccess, verifyOtpData, isVerifyOtpError, currentUser, dispatch, addHostDetails]);

  const currentUsername = currentUser?.username;
  const currentUserEmail = currentUser?.email;

  const handleSendCodeToEmail = useCallback(() => {
    if (!currentUsername || !currentUserEmail) return;
    setBtnEmailLoading(true);
    setReSendCodeMsg(false);
    sendSecurityCodeToEmail({ username: currentUsername, email: currentUserEmail });
  }, [currentUsername, currentUserEmail, sendSecurityCodeToEmail]);

  const handleVerifyCode = useCallback(() => {
    if (!currentUserEmail || !code) return;
    setVerifyBtnLoding(true);
    verifySecurityCode({ email: currentUserEmail, code });
  }, [currentUserEmail, code, verifySecurityCode]);

  const handleBack = useCallback(() => {
    setVerifyBlock(false);
    setCodeToEmail(false);
    setCodeToMobile(false);
    setCode("");
    setCodeError("");
    setReSendCodeMsg(false);
  }, []);

  const handleResendCode = useCallback(() => {
    if (!currentUsername || !currentUserEmail) return;
    setReSendCodeBtnLoding(true);
    setReSendCodeMsg(false);
    if (codeToEmail && !codeToMobile) {
      sendSecurityCodeToEmail({ username: currentUsername, email: currentUserEmail });
    }
  }, [currentUsername, currentUserEmail, codeToEmail, codeToMobile, sendSecurityCodeToEmail]);

  const handleCodeChange = useCallback(({ currentTarget }) => {
    setCode(currentTarget.value);
    if (codeError) {
      setCodeError("");
    }
  }, [codeError]);

  return (
    <div className="login-page">
      <div className="login-shell verify-shell">
        {/* <div className="login-top-banner">
          <i className="fa fa-shield" aria-hidden="true"></i>
          <span>Human Resources Information Management System (HRIMS)</span>
        </div> */}
        <section className="login-hero" aria-label="Identity verification overview">
          <p className="login-kicker">Security Check</p>
          <h1>Verify your identity</h1>
          <p className="login-copy">
            We use a one-time security code before opening your WorkFINITY&reg; workspace.
          </p>
          <div className="login-hero-account">
            <div>
              <span>User Type</span>
              <strong>{usrType || "Portal user"}</strong>
            </div>
            <div>
              <span>Username</span>
              <strong>{currentUser?.username || "Pending"}</strong>
            </div>
          </div>
        </section>

        <section className="login-panel" aria-label="Verify identity">
          <div className="login-card verify-card">
            <div className="login-card-header">
              <p className="login-step-label">Multi-factor authentication</p>
              <h2>Security code</h2>
              {!codeToEmail && !codeToMobile && (
                <p>Select where you want to receive your one-time security code.</p>
              )}
              {codeToEmail && (
                <p>
                  We have sent a security code to <strong>{maskEmail(currentUser?.email)}</strong>.
                </p>
              )}
              {codeToMobile && (
                <p>
                  We have sent a security code to the mobile number ending in{" "}
                  <strong>
                    {currentUser?.mobile?.slice(currentUser.mobile.length - 4)}
                  </strong>.
                </p>
              )}
            </div>

            {!verifyBlock && (
              <div className="verify-choice">
                <button
                  className="btn btn-outline-default verify-choice-button"
                  disabled={btnEmailLoading || !currentUser?.email}
                  onClick={handleSendCodeToEmail}
                >
                  {btnEmailLoading ? (
                    <i className="fa fa-refresh fa-spin mr-2" aria-hidden="true"></i>
                  ) : (
                    <i className="fa fa-envelope-o mr-2" aria-hidden="true"></i>
                  )}
                  {btnEmailLoading ? "Sending code..." : "Send to my email"}
                </button>
              </div>
            )}

            {verifyBlock && (
              <div className="verify-form">
                <div className="form-group">
                  <label htmlFor="code" className="form-control-label">
                    Enter your security code
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    className="form-control"
                    value={code}
                    placeholder="Security code"
                    onChange={handleCodeChange}
                  />
                  {Boolean(codeError) && (
                    <div className="alert alert-danger mt-2 mb-0">{codeError}</div>
                  )}
                </div>

                <div className="verify-actions">
                  <button
                    className="btn btn-outline-default"
                    disabled={!Boolean(code) || verifyBtnLoding}
                    onClick={handleVerifyCode}
                  >
                    {verifyBtnLoding ? (
                      <React.Fragment>
                        <i className="fa fa-refresh fa-spin mr-2" aria-hidden="true"></i>
                        Verifying...
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <i className="fa fa-check mr-2" aria-hidden="true"></i>
                        Verify
                      </React.Fragment>
                    )}
                  </button>
                  <button
                    className="btn btn-link login-link"
                    disabled={verifyBtnLoding}
                    onClick={handleBack}
                  >
                    Back
                  </button>
                </div>

                <div className="verify-resend">
                  <span>Did not receive the code?</span>
                  <button
                    className="btn btn-sm btn-link"
                    disabled={reSendCodeBtnLoding}
                    onClick={handleResendCode}
                  >
                    {reSendCodeBtnLoding && (
                      <i className="fa fa-refresh fa-spin mr-2" aria-hidden="true"></i>
                    )}
                    {reSendCodeBtnLoding ? "Resending..." : "Resend Code"}
                  </button>
                  {reSendCodeMsg && (
                    <strong>Security code sent successfully.</strong>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
        <div className="login-saas-banner">
          <i className="fa fa-cloud" aria-hidden="true"></i>
          <span>
            SaaS platform hosted in Microsoft Azure Government Account - a FedRAMP certified cloud infrastructure
          </span>
        </div>
        <div className="login-support-row">
          <a
            className="login-support-link"
            href="mailto:Support@workfinity.com"
            title="Support@workfinity.com"
          >
            <i className="fa fa-life-ring" aria-hidden="true"></i>
            <span>Customer Support</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ValidateUser;
